import { useMemo, memo } from 'react';
import { subDays, eachDayOfInterval, format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { BarChart2 } from 'lucide-react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/client/components/ui/chart';
import {
  BAR_RADIUS_VERTICAL,
  BAR_MAX_SIZE,
  CHART_COLOR_PRIMARY,
  CHART_HEIGHTS,
  CHART_MARGINS,
} from '@/client/lib/chart-constants';
import { useTimezone } from '@/client/hooks/use-timezone';

interface TimelineDataPoint {
  date: string;
  interactions: number;
}

interface VisitsChartProps {
  data: TimelineDataPoint[];
  days?: number;
}

const chartConfig = {
  clicks: {
    label: 'Clicks',
    color: CHART_COLOR_PRIMARY,
  },
} satisfies ChartConfig;

export const VisitsChart = memo(function VisitsChart({
  data: timelineData,
  days = 14,
}: VisitsChartProps) {
  const { effectiveTimezone } = useTimezone();
  const timezone = effectiveTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const chartData = useMemo(() => {
    // Check if we are dealing with hourly data (check first date format or if days <= 1)

    const isHourly = timelineData.length > 0 && timelineData[0]?.date.includes(':');

    if (isHourly) {
      // For hourly data, use the data points directly (they are already bucketed by hour from backend)
      // Just ensure we have a continuous timeline if needed, but for now map directly
      return timelineData.map((point) => {
        // point.date is "YYYY-MM-DD HH:00"
        const dateObj = new Date(point.date.replace(' ', 'T'));
        return {
          date: point.date,
          clicks: point.interactions,
          // Format as "10 PM", "11 PM"
          displayDate: formatInTimeZone(dateObj, timezone, 'h a'),
        };
      });
    }

    // Daily logic
    const now = new Date();
    const todayInTz = toZonedTime(now, timezone);
    const endDate = todayInTz;
    const startDate = subDays(endDate, days - 1);

    // Create a map of all days in the range using LOCAL dates (yyyy-MM-dd format)
    const dayMap = new Map<string, { date: string; clicks: number }>();

    eachDayOfInterval({ start: startDate, end: endDate }).forEach((date) => {
      const key = format(date, 'yyyy-MM-dd');
      dayMap.set(key, { date: key, clicks: 0 });
    });

    // Fill in data
    timelineData.forEach((stat) => {
      const localDateKey = stat.date;
      const day = dayMap.get(localDateKey);
      if (day) {
        day.clicks += stat.interactions;
      }
    });

    return Array.from(dayMap.values()).map((day) => ({
      ...day,
      displayDate: formatInTimeZone(new Date(`${day.date}T12:00:00`), timezone, 'MMM d'),
    }));
  }, [timelineData, days, timezone]);

  const totalClicks = chartData.reduce((sum, d) => sum + d.clicks, 0);

  if (timelineData.length === 0) {
    return (
      <div className="h-48 flex flex-col items-center justify-center text-muted-foreground">
        <BarChart2 className="size-8 mb-2 text-muted-foreground/50" />
        <p className="text-sm">No visit data available</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Click activity will appear here</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-muted-foreground">Daily Clicks</h4>
        <span className="text-sm font-medium text-muted-foreground">{totalClicks} total</span>
      </div>

      <ChartContainer
        config={chartConfig}
        className="w-full"
        style={{ height: CHART_HEIGHTS.medium }}
      >
        <BarChart data={chartData} margin={CHART_MARGINS.vertical}>
          <CartesianGrid
            vertical={false}
            strokeDasharray="3 3"
            stroke="var(--border)"
            opacity={0.4}
          />
          <XAxis
            dataKey="displayDate"
            tickLine={false}
            axisLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
            tickMargin={8}
            fontSize={10}
            minTickGap={30}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: 'var(--border)', strokeWidth: 1 }}
            tickMargin={4}
            fontSize={10}
            width={40}
            tickFormatter={(value) => (value === 0 ? '' : value)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(value) => value}
                formatter={(value, _name) => (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: 'var(--chart-1)' }}
                    />
                    <span className="text-muted-foreground">Clicks</span>
                    <span className="font-mono font-medium tabular-nums ml-auto">
                      {value?.toLocaleString()}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Bar
            dataKey="clicks"
            fill="var(--color-clicks)"
            radius={BAR_RADIUS_VERTICAL}
            maxBarSize={BAR_MAX_SIZE}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
});
